import { calendarDate, formatMinistryDate } from '../utils/ministryDates';
import AdminLogin from '../components/AdminLogin';
import { useAuth } from '../contexts/AuthContext';
import { useEffect, useState } from 'react';
import { eventUtils } from '../lib/eventUtils';
import Calendar from '../components/Calendar/Calendar';
import EventDetailModal from '../components/Calendar/EventDetailModal';
import { getEventStyle } from '../lib/eventTypes';
import { CalendarClock, MapPin, CalendarDays, Plus, X, Lock, LogIn, Sparkles, ImagePlus } from 'lucide-react';

const inputCls = 'w-full rounded-lg border border-gray-200 px-4 py-2 focus:border-purple-400 focus:ring-2 focus:ring-purple-100';

const EventForm = ({ onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    title: '', description: '', startDate: '', endDate: '', location: '', type: 'event', image: null,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const set = (k) => (e) => setFormData((p) => ({ ...p, [k]: e.target.value }));
  const setImage = (e) => {
    const file = e.target.files?.[0] || null;
    setFormData((p) => ({ ...p, image: file }));
    setImagePreview(file ? URL.createObjectURL(file) : null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-purple-950/60 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full text-gray-400 transition-all hover:rotate-90 hover:bg-gray-100">
          <X className="h-5 w-5" />
        </button>
        <h2 className="mb-6 font-display text-2xl font-semibold text-gray-900">Add New Event</h2>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(formData); }} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Title</label>
            <input type="text" required value={formData.title} onChange={set('title')} className={inputCls} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
            <textarea required rows={3} value={formData.description} onChange={set('description')} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Start date & time (Antigua)</label>
              <input type="datetime-local" required value={formData.startDate} onChange={set('startDate')} className={inputCls} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">End date & time (Antigua)</label>
              <input type="datetime-local" value={formData.endDate} onChange={set('endDate')} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Location</label>
            <input type="text" value={formData.location} onChange={set('location')} className={inputCls} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Event type</label>
            <select value={formData.type} onChange={set('type')} className={inputCls}>
              <option value="event">Event</option>
              <option value="meeting">Meeting</option>
              <option value="celebration">Celebration</option>
              <option value="fundraiser">Fundraiser</option>
              <option value="deadline">Deadline</option>
              <option value="general">General</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Image (optional)</label>
            {imagePreview ? (
              <div className="relative overflow-hidden rounded-lg border border-gray-200">
                <img src={imagePreview} alt="Preview" className="h-40 w-full object-cover" />
                <button type="button" onClick={() => { setFormData((p) => ({ ...p, image: null })); setImagePreview(null); }}
                        className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-black/50 text-white hover:bg-black/70">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-500 transition-colors hover:border-purple-300 hover:text-purple-600">
                <ImagePlus className="h-4 w-4" />
                Choose an image…
                <input type="file" accept="image/*" onChange={setImage} className="sr-only" />
              </label>
            )}
          </div>
          <button type="submit" disabled={isLoading} className="w-full rounded-lg bg-purple-600 px-4 py-2.5 font-semibold text-white transition-colors hover:bg-purple-700 disabled:opacity-50">
            {isLoading ? 'Creating…' : 'Create Event'}
          </button>
        </form>
      </div>
    </div>
  );
};


const UpcomingEventCard = ({ event, isNext, onClick }) => {
  const style = getEventStyle(event.type);
  const date = calendarDate(event.startDate);
  return (
    <button onClick={onClick} className="group relative flex w-full gap-3 overflow-hidden rounded-xl border border-gray-100 bg-white p-3 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-purple-100 hover:shadow-md">
      <span className={`absolute inset-y-0 left-0 w-1 ${style.solid}`} />
      {/* Date block — over the event image when available */}
      <div className="relative flex h-14 w-12 flex-shrink-0 flex-col items-center justify-center overflow-hidden rounded-lg bg-gray-50">
        {event.imageUrl && (
          <>
            <img src={event.imageUrl} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-purple-900/55" />
          </>
        )}
        <span className={`relative text-[10px] font-bold uppercase tracking-wide ${event.imageUrl ? 'text-white/90' : 'text-purple-600'}`}>
          {date.toLocaleDateString(undefined, { month: 'short' })}
        </span>
        <span className={`relative font-display text-xl font-semibold leading-none ${event.imageUrl ? 'text-white' : 'text-gray-900'}`}>{date.getDate()}</span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="truncate font-semibold text-gray-900 group-hover:text-purple-700">{event.title}</h3>
          {isNext && <span className="flex-shrink-0 rounded-full bg-purple-600 px-2 py-0.5 text-[10px] font-bold text-white">NEXT</span>}
        </div>
        <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-500">
          <CalendarClock className="h-3 w-3" />
          {event.dateLabel || formatMinistryDate(event.startDate, { weekday: 'short', hour: 'numeric', minute: '2-digit' })}
        </p>
        {event.location && (
          <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-gray-500">
            <MapPin className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{event.location}</span>
          </p>
        )}
        <span className={`mt-1.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ring-1 ${style.chip}`}>
          {event.type || 'general'}
        </span>
      </div>
    </button>
  );
};

export default function CalendarPage() {
    const { isAdmin, logout } = useAuth();
  const [events, setEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loadEvents = async () => {
    try {
      const allEvents = await eventUtils.getEvents();
      setEvents(allEvents);
      const now = new Date();
      setUpcomingEvents(
        allEvents
          .filter((e) => new Date(e.startDate) >= now)
          .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
          .slice(0, 6)
      );
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };

  useEffect(() => { loadEvents(); }, []);

  const handleEventSubmit = async (formData) => {
    try {
      setIsLoading(true);
      await eventUtils.createEvent(formData);
      await loadEvents();
      setShowEventForm(false);
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Failed to create event');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEventDelete = async (eventId) => {
    try {
      await eventUtils.deleteEvent(eventId);
      await loadEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event');
    }
  };

  const handleLogout = () => logout();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <header className="relative overflow-hidden bg-gradient-to-br from-purple-900 via-purple-800 to-purple-950 pt-32 pb-28">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-10 left-1/4 h-72 w-72 rounded-full bg-purple-500/20 blur-3xl" />
          <div className="absolute bottom-0 right-1/4 h-64 w-64 rounded-full bg-gold-400/10 blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-purple-100 backdrop-blur-sm">
            <Sparkles className="h-4 w-4" />
            What&rsquo;s On
          </span>
          <h1 className="mt-5 font-display text-5xl sm:text-6xl font-semibold text-white">
            Community{' '}
            <span className="bg-gradient-to-r from-gold-200 to-white bg-clip-text italic text-transparent">Calendar</span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-purple-100/80">
            Stay connected with our upcoming events, fundraisers, and celebrations.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-white backdrop-blur-sm">
              <CalendarDays className="h-4 w-4" />
              {events.length} events scheduled
            </span>
            {isAdmin && (
              <>
                <button onClick={() => setShowEventForm(true)} className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2 text-sm font-semibold text-purple-800 shadow transition-all hover:-translate-y-0.5 hover:shadow-lg">
                  <Plus className="h-4 w-4" /> Add Event
                </button>
                <button onClick={handleLogout} className="rounded-full px-4 py-2 text-sm font-medium text-white/80 transition-colors hover:bg-white/10 hover:text-white">
                  Sign Out
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="relative z-10 mx-auto -mt-12 max-w-7xl px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-4 lg:gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-28 rounded-2xl border border-gray-100 bg-white shadow-sm">
              <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-4">
                <CalendarClock className="h-5 w-5 text-purple-600" />
                <h2 className="font-display text-lg font-semibold text-gray-900">Upcoming</h2>
              </div>
              <div className="max-h-[calc(100vh-240px)] space-y-3 overflow-y-auto p-4 scrollbar-hide">
                {upcomingEvents.length > 0 ? (
                  upcomingEvents.map((event, index) => (
                    <UpcomingEventCard key={event.id} event={event} isNext={index === 0} onClick={() => setSelectedEvent(event)} />
                  ))
                ) : (
                  <div className="py-10 text-center">
                    <CalendarClock className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                    <p className="text-sm text-gray-500">No upcoming events yet.</p>
                  </div>
                )}
              </div>
            </div>
          </aside>

          {/* Calendar */}
          <div className="lg:col-span-3">
            <Calendar
              events={events}
              isAdmin={isAdmin}
              onAddEvent={handleEventSubmit}
              onDeleteEvent={handleEventDelete}
            />
          </div>
        </div>
      </div>

      {/* Admin login button */}
      {!isAdmin && (
        <button
          onClick={() => setShowLoginForm(true)}
          className="fixed bottom-6 right-6 z-40 grid h-12 w-12 place-items-center rounded-full border border-gray-200/50 bg-white/90 shadow-lg backdrop-blur transition-all hover:-translate-y-0.5 hover:bg-purple-50"
          aria-label="Admin Login"
        >
          <Lock className="h-5 w-5 text-gray-600" />
        </button>
      )}

      {showEventForm && (
        <EventForm onClose={() => setShowEventForm(false)} onSubmit={handleEventSubmit} isLoading={isLoading} />
      )}
      {showLoginForm && (
        <AdminLogin onClose={() => setShowLoginForm(false)} />
      )}

      {/* Shared event details modal (sidebar clicks) */}
      <EventDetailModal
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        isAdmin={isAdmin}
        onDelete={handleEventDelete}
      />
    </div>
  );
}
